import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { configureApi, get, type PlanLimitError } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { useUiStore } from '@/stores/uiStore';
import type { Session } from '@/types/models';
import { SplashScreen } from '@/components/layout/SplashScreen';

/**
 * Wires the API client to the stores, then resolves the session.
 *
 * GET /me is the single source of truth: capabilities, features, subscription
 * state and the tenant's currency all arrive in one request. The app shows a
 * splash until it resolves rather than rendering a shell that then rearranges
 * itself — a nav that appears and then loses half its items reads as a bug.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [configured, setConfigured] = useState(false);

  const token = useAuthStore((s) => s.token);
  const session = useAuthStore((s) => s.session);
  const setSession = useAuthStore((s) => s.setSession);
  const clear = useAuthStore((s) => s.clear);
  const openUpgradePrompt = useUiStore((s) => s.openUpgradePrompt);

  useEffect(() => {
    configureApi({
      getToken: () => useAuthStore.getState().token,
      getOutletId: () => useAuthStore.getState().activeOutletId,

      onUnauthenticated: () => {
        const hadToken = useAuthStore.getState().token !== null;
        useAuthStore.getState().clear();

        // Preserve where they were, so signing back in returns them there
        // rather than dumping them on the dashboard.
        if (hadToken && !window.location.pathname.startsWith('/login')) {
          const next = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/login?next=${next}`;
        }
      },

      onPlanLimit: (error: PlanLimitError) => openUpgradePrompt(error),

      onForbidden: (message) => toast.error(message),

      onThrottled: (message, retryAfter) =>
        toast.error(message, {
          description: `Try again in ${retryAfter} second${retryAfter === 1 ? '' : 's'}.`,
        }),
    });

    setConfigured(true);
  }, [openUpgradePrompt]);

  const { data, isError } = useQuery({
    queryKey: ['session'],
    queryFn: () => get<Session>('/me'),
    enabled: configured && token !== null,
    // The session is refetched deliberately (after a role change, an outlet
    // switch, a subscription payment) rather than on a timer.
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (data) setSession(data);
  }, [data, setSession]);

  useEffect(() => {
    // A token that no longer resolves is a token worth discarding, so the
    // user gets a clean login rather than a half-broken session.
    if (isError) clear();
  }, [isError, clear]);

  if (!configured) return <SplashScreen />;

  /*
   * Wait for the session to be IN THE STORE, not merely fetched.
   *
   * `setSession` runs in an effect, so there is one render between the query
   * resolving and the store holding a session. Gating on `isLoading` alone let
   * the router render in that gap with `session === null`, and ProtectedRoute
   * — correctly, given what it could see — bounced to /login. The session then
   * landed, PublicOnlyRoute fired, and the user arrived at /dashboard.
   *
   * The visible symptom was that refreshing any page threw you back to the
   * dashboard. On a till, where F5 is the universal fix for everything, that
   * is a page you cannot stay on.
   *
   * `isError` is the escape: the effect below clears the token, so this
   * condition goes false on the next render rather than splashing forever.
   */
  if (token && !session && !isError) return <SplashScreen />;

  return <>{children}</>;
}
